import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShieldCheckIcon,
  PlusIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  TagIcon,
  BookOpenIcon,
  CheckCircleIcon,
  LightBulbIcon,
  ClockIcon,
  UserIcon,
  PlayIcon,
  VideoCameraIcon,
  EyeIcon,
  ChartBarIcon,
  Squares2X2Icon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import {
  collection,
  query,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  orderBy,
  increment,
  setDoc,
  getDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { firestore, storage } from '../../../database/firebase';
import { useAuth } from '../../../context/AuthContext';
import { useRole } from '../../../hooks/useRole';
import { useToast } from '../../../context/ToastContext';
import { DeleteConfirmation } from '../../ui/DeleteConfirmation';
import { Card, CardContent } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Input, Textarea, Label, Select } from '../../ui/Input';
import { Modal, ModalFooter } from '../../ui/Modal';

const BestPractices = () => {
  const { currentUser } = useAuth();
  const { userRole } = useRole();
  const toast = useToast();
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [practiceToDelete, setPracticeToDelete] = useState(null);
  const [editingPractice, setEditingPractice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [savingPractice, setSavingPractice] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [newPractice, setNewPractice] = useState({
    title: '',
    description: '',
    category: 'coaching',
    content: '',
    tags: [],
    steps: [],
    videoUrl: '',
  });

  const isCoach = userRole === 'coach' || userRole === 'admin' || userRole === 'superAdmin';

  useEffect(() => {
    setLoading(true);
    let unsubscribe = null;

    // Helper function to process snapshot data
    const processSnapshot = snapshot => {
      const practicesData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Handle Firestore timestamp conversion
          createdAt: data.createdAt?.toDate
            ? data.createdAt.toDate()
            : data.createdAt?.seconds
              ? new Date(data.createdAt.seconds * 1000)
              : data.createdAt || new Date(),
          updatedAt: data.updatedAt?.toDate
            ? data.updatedAt.toDate()
            : data.updatedAt?.seconds
              ? new Date(data.updatedAt.seconds * 1000)
              : data.updatedAt || new Date(),
        };
      });

      // Sort manually by createdAt (descending)
      practicesData.sort((a, b) => {
        const aDate = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const bDate = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return bDate.getTime() - aDate.getTime();
      });

      setPractices(practicesData);
      setLoading(false);
    };

    // Try query with orderBy first
    const tryQueryWithOrderBy = () => {
      const practicesQuery = query(
        collection(firestore, 'bestPractices'),
        orderBy('createdAt', 'desc')
      );

      unsubscribe = onSnapshot(practicesQuery, processSnapshot, error => {
        console.error('Error listening to best practices:', error);

        // If orderBy fails due to missing index, try without orderBy
        if (error.code === 'failed-precondition' || error.message?.includes('index')) {
          console.info('Index not yet created, using fallback query');
          tryQueryWithoutOrderBy();
        } else {
          toast.error('Failed to load best practices', 'Error');
          setLoading(false);
        }
      });
    };

    // Fallback query without orderBy
    const tryQueryWithoutOrderBy = () => {
      if (unsubscribe) unsubscribe(); // Clean up previous listener

      const simpleQuery = query(collection(firestore, 'bestPractices'));

      unsubscribe = onSnapshot(simpleQuery, processSnapshot, fallbackError => {
        console.error('Error in fallback query:', fallbackError);
        toast.error('Failed to load best practices', 'Error');
        setLoading(false);
      });
    };

    // Start with orderBy query
    tryQueryWithOrderBy();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const categories = [
    { id: 'all', name: 'All Categories', color: 'gray' },
    { id: 'coaching', name: 'Coaching', color: 'emerald' },
    { id: 'communication', name: 'Communication', color: 'blue' },
    { id: 'leadership', name: 'Leadership', color: 'purple' },
    { id: 'business', name: 'Business Development', color: 'orange' },
    { id: 'strategy', name: 'Strategy', color: 'indigo' },
  ];

  const getCategoryColor = categoryId => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return 'gray';

    const colorMap = {
      emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      purple: 'bg-purple-100 text-purple-800 border-purple-200',
      orange: 'bg-orange-100 text-orange-800 border-orange-200',
      indigo: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      gray: 'bg-gray-100 text-gray-800 border-gray-200',
    };

    return colorMap[category.color] || colorMap.gray;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!newPractice.title || !newPractice.description) {
      toast.warning('Please fill in all required fields', 'Validation Error');
      return;
    }

    setSavingPractice(true);
    try {
      // Fetch user profile to get firstname and lastname
      let authorName = currentUser.displayName || currentUser.email;
      let authorFirstName = '';
      let authorLastName = '';

      try {
        // Try to get coach profile first
        const coachDoc = await getDoc(doc(firestore, 'coaches', currentUser.uid));
        if (coachDoc.exists()) {
          const coachData = coachDoc.data();
          authorFirstName = coachData.firstname || '';
          authorLastName = coachData.lastname || '';
          if (authorFirstName || authorLastName) {
            authorName = `${authorFirstName} ${authorLastName}`.trim();
          }
        } else {
          // Try admin collection
          const adminDoc = await getDoc(doc(firestore, 'admin', currentUser.uid));
          if (adminDoc.exists()) {
            const adminData = adminDoc.data();
            authorFirstName = adminData.firstname || '';
            authorLastName = adminData.lastname || '';
            if (authorFirstName || authorLastName) {
              authorName = `${authorFirstName} ${authorLastName}`.trim();
            }
          } else {
            // Try moderator collection
            const moderatorDoc = await getDoc(doc(firestore, 'moderator', currentUser.uid));
            if (moderatorDoc.exists()) {
              const moderatorData = moderatorDoc.data();
              authorFirstName = moderatorData.firstname || '';
              authorLastName = moderatorData.lastname || '';
              if (authorFirstName || authorLastName) {
                authorName = `${authorFirstName} ${authorLastName}`.trim();
              }
            }
          }
        }
      } catch (error) {
        console.warn('Failed to fetch user profile:', error);
        // Continue with email as fallback
      }

      let videoUrl = newPractice.videoUrl;

      // Upload video if a new file is selected
      if (videoFile) {
        setUploadingVideo(true);
        try {
          const videoRef = ref(storage, `bestPractices/videos/${Date.now()}_${videoFile.name}`);
          await uploadBytes(videoRef, videoFile);
          videoUrl = await getDownloadURL(videoRef);

          // Delete old video if editing and replacing
          if (editingPractice?.videoUrl && editingPractice.videoUrl !== newPractice.videoUrl) {
            try {
              const oldVideoRef = ref(storage, editingPractice.videoUrl);
              await deleteObject(oldVideoRef);
            } catch (error) {
              console.warn('Failed to delete old video:', error);
            }
          }
        } catch (error) {
          console.error('Error uploading video:', error);
          toast.error('Failed to upload video', 'Error');
          setUploadingVideo(false);
          return;
        }
        setUploadingVideo(false);
      }

      const practiceData = {
        ...newPractice,
        videoUrl: videoUrl || newPractice.videoUrl || '',
        createdBy: currentUser.email,
        createdByName: authorName,
        createdByFirstName: authorFirstName,
        createdByLastName: authorLastName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // Initialize analytics
        views: editingPractice?.views || 0,
        clicks: editingPractice?.clicks || 0,
        lastViewedAt: editingPractice?.lastViewedAt || null,
      };

      if (editingPractice) {
        await updateDoc(doc(firestore, 'bestPractices', editingPractice.id), {
          ...practiceData,
          updatedAt: serverTimestamp(),
        });
        toast.success('Best practice updated successfully!');
      } else {
        await addDoc(collection(firestore, 'bestPractices'), practiceData);
        toast.success('Best practice created successfully!');
      }

      setShowModal(false);
      setEditingPractice(null);
      setNewPractice({
        title: '',
        description: '',
        category: 'coaching',
        content: '',
        tags: [],
        steps: [],
        videoUrl: '',
      });
      setVideoFile(null);
      setVideoPreview(null);
    } catch (error) {
      console.error('Error saving best practice:', error);
      toast.error('Failed to save best practice', 'Error');
    } finally {
      setSavingPractice(false);
    }
  };

  const handleDeleteClick = practice => {
    setPracticeToDelete(practice);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!practiceToDelete) return;

    try {
      // Delete video from storage if exists
      if (practiceToDelete.videoUrl) {
        try {
          const videoRef = ref(storage, practiceToDelete.videoUrl);
          await deleteObject(videoRef);
        } catch (error) {
          console.warn('Failed to delete video:', error);
          // Continue with practice deletion even if video deletion fails
        }
      }

      await deleteDoc(doc(firestore, 'bestPractices', practiceToDelete.id));
      toast.success('Best practice deleted successfully!');
      setShowDeleteModal(false);
      setPracticeToDelete(null);
    } catch (error) {
      console.error('Error deleting best practice:', error);
      toast.error('Failed to delete best practice', 'Error');
    }
  };

  const handleEdit = practice => {
    setEditingPractice(practice);
    // Normalize steps to ensure they're objects
    const normalizedSteps = (practice.steps || []).map(step => normalizeStep(step));
    setNewPractice({
      title: practice.title || '',
      description: practice.description || '',
      category: practice.category || 'coaching',
      content: practice.content || '',
      tags: practice.tags || [],
      steps: normalizedSteps,
      videoUrl: practice.videoUrl || '',
    });
    setVideoFile(null);
    setVideoPreview(practice.videoUrl || null);
    setShowModal(true);
  };

  const handleCardClick = async practice => {
    setSelectedPractice(practice);
    setShowDetailModal(true);

    // Track view analytics
    try {
      const practiceRef = doc(firestore, 'bestPractices', practice.id);
      await updateDoc(practiceRef, {
        views: increment(1),
        lastViewedAt: serverTimestamp(),
      });

      // Log detailed analytics event
      await addDoc(collection(firestore, 'bestPracticesAnalytics'), {
        practiceId: practice.id,
        practiceTitle: practice.title,
        userId: currentUser?.uid || 'anonymous',
        userEmail: currentUser?.email || 'anonymous',
        userRole: userRole || 'unknown',
        action: 'view',
        timestamp: serverTimestamp(),
        metadata: {
          category: practice.category,
          hasVideo: !!practice.videoUrl,
          hasContent: !!practice.content,
        },
      });
    } catch (error) {
      console.error('Error tracking analytics:', error);
    }
  };

  const handleVideoUpload = e => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('video/')) {
        toast.error('Please select a valid video file', 'Error');
        return;
      }

      // Validate file size (max 100MB)
      if (file.size > 100 * 1024 * 1024) {
        toast.error('Video file size must be less than 100MB', 'Error');
        return;
      }

      setVideoFile(file);
      const videoUrl = URL.createObjectURL(file);
      setVideoPreview(videoUrl);
    }
  };

  const addStep = () => {
    setNewPractice({
      ...newPractice,
      steps: [...(newPractice.steps || []), { title: '', description: '' }],
    });
  };

  const updateStep = (index, field, value) => {
    const updatedSteps = [...(newPractice.steps || [])];
    // Handle backward compatibility - convert string steps to objects
    if (typeof updatedSteps[index] === 'string') {
      updatedSteps[index] = { title: updatedSteps[index], description: '' };
    }
    updatedSteps[index] = {
      ...updatedSteps[index],
      [field]: value,
    };
    setNewPractice({ ...newPractice, steps: updatedSteps });
  };

  const removeStep = index => {
    const updatedSteps = newPractice.steps.filter((_, i) => i !== index);
    setNewPractice({ ...newPractice, steps: updatedSteps });
  };

  // Helper function to normalize step data (handle backward compatibility)
  const normalizeStep = step => {
    if (typeof step === 'string') {
      return { title: step, description: '' };
    }
    return {
      title: step?.title || '',
      description: step?.description || '',
    };
  };

  const filteredPractices = practices.filter(practice => {
    const matchesSearch =
      practice.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      practice.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      practice.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || practice.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-success/20 border-t-success"></div>
              <p className="font-medium text-text-secondary">Loading best practices...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-4 sm:p-6">
        {/* Header */}
        <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg">
              <ShieldCheckIcon className="h-7 w-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Best Practices</h2>
              <p className="text-sm text-gray-500">
                Industry best practices and proven methodologies
              </p>
            </div>
          </div>
          {isCoach && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                setEditingPractice(null);
                setNewPractice({
                  title: '',
                  description: '',
                  category: 'coaching',
                  content: '',
                  tags: [],
                  steps: [],
                  videoUrl: '',
                });
                setVideoFile(null);
                setVideoPreview(null);
                setShowModal(true);
              }}
              className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 px-5 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-emerald-600 hover:to-teal-700 hover:shadow-xl"
            >
              <PlusIcon className="h-5 w-5" />
              <span className="hidden sm:inline">Add Practice</span>
            </motion.button>
          )}
        </div>

        {/* Search and Filters */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search best practices..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 pl-10 text-sm transition-all focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            />
          </div>
          <select
            value={selectedCategory}
            onChange={e => setSelectedCategory(e.target.value)}
            className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium transition-all focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          >
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>

          {/* View Mode Toggle */}
          <div className="flex rounded-xl border border-gray-300 bg-white p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                viewMode === 'grid'
                  ? 'bg-emerald-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <Squares2X2Icon className="h-4 w-4" />
              <span className="hidden sm:inline">Grid</span>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                viewMode === 'table'
                  ? 'bg-emerald-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <TableCellsIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Table</span>
            </button>
          </div>
        </div>

        {/* Practices View */}
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {filteredPractices.map((practice, index) => (
                <motion.div
                  key={practice.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: -20 }}
                  transition={{
                    delay: index * 0.05,
                    layout: { duration: 0.3 },
                  }}
                  onClick={() => handleCardClick(practice)}
                  className="group relative cursor-pointer overflow-hidden rounded-xl border border-border bg-surface shadow-sm transition-all hover:scale-[1.02] hover:border-success/20 hover:shadow-lg"
                >
                  {/* Gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-success/5 via-transparent to-accent/5 opacity-0 transition-opacity group-hover:opacity-100" />

                  <div className="relative p-6">
                    <div className="mb-4 flex items-start justify-between">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-success to-accent shadow-lg">
                        <ShieldCheckIcon className="h-6 w-6 text-white" />
                      </div>
                      {isCoach && (
                        <div className="flex items-center gap-1">
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={e => {
                              e.stopPropagation();
                              handleEdit(practice);
                            }}
                            className="rounded-lg p-2 text-text-tertiary transition-colors hover:bg-surface-elevated hover:text-success"
                            aria-label="Edit practice"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={e => {
                              e.stopPropagation();
                              handleDeleteClick(practice);
                            }}
                            className="rounded-lg p-2 text-text-tertiary transition-colors hover:bg-error/10 hover:text-error"
                            aria-label="Delete practice"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </motion.button>
                        </div>
                      )}
                    </div>

                    <h3 className="mb-2 text-xl font-bold text-text-primary transition-colors group-hover:text-success">
                      {practice.title}
                    </h3>
                    <p className="mb-4 line-clamp-3 text-sm leading-relaxed text-text-secondary">
                      {practice.description}
                    </p>

                    <div className="mb-4 flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium ${getCategoryColor(practice.category)}`}
                      >
                        <TagIcon className="h-3 w-3" />
                        {categories.find(c => c.id === practice.category)?.name ||
                          practice.category}
                      </span>
                      {practice.createdAt && (
                        <div className="flex items-center gap-1 text-xs text-text-tertiary">
                          <ClockIcon className="h-3 w-3" />
                          <span>
                            {practice.createdAt?.toDate
                              ? new Date(practice.createdAt.toDate()).toLocaleDateString()
                              : 'Recently'}
                          </span>
                        </div>
                      )}
                    </div>

                    {practice.steps && practice.steps.length > 0 && (
                      <div className="mb-4 rounded-lg border border-success/20 bg-success/5 p-4">
                        <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-success">
                          <CheckCircleIcon className="h-4 w-4" />
                          <span>{practice.steps.length} Steps</span>
                        </div>
                        <ul className="space-y-2">
                          {practice.steps.slice(0, 3).map((step, idx) => {
                            const normalizedStep = normalizeStep(step);
                            return (
                              <li
                                key={idx}
                                className="flex items-start gap-2 text-xs text-text-secondary"
                              >
                                <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-success/20 text-[10px] font-bold text-success">
                                  {idx + 1}
                                </span>
                                <div className="flex-1">
                                  <span className="font-semibold text-text-primary">
                                    {normalizedStep.title}
                                  </span>
                                  {normalizedStep.description && (
                                    <span className="ml-1 text-text-secondary">
                                      - {normalizedStep.description}
                                    </span>
                                  )}
                                </div>
                              </li>
                            );
                          })}
                          {practice.steps.length > 3 && (
                            <li className="text-xs font-medium text-success">
                              +{practice.steps.length - 3} more steps
                            </li>
                          )}
                        </ul>
                      </div>
                    )}

                    {practice.createdBy && (
                      <div className="mb-4 flex items-center gap-2 text-xs text-text-tertiary">
                        <UserIcon className="h-3 w-3" />
                        <span>
                          By{' '}
                          {practice.createdByFirstName || practice.createdByLastName
                            ? `${practice.createdByFirstName || ''} ${practice.createdByLastName || ''}`.trim()
                            : practice.createdByName || ''}{' '}
                          ({practice.createdBy})
                        </span>
                      </div>
                    )}

                    {/* Analytics Badge */}
                    {(practice.views > 0 || practice.clicks > 0) && (
                      <div className="mb-4 flex items-center gap-3 text-xs text-text-tertiary">
                        <div className="flex items-center gap-1">
                          <EyeIcon className="h-3 w-3" />
                          <span>{practice.views || 0} views</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <ChartBarIcon className="h-3 w-3" />
                          <span>{practice.clicks || 0} clicks</span>
                        </div>
                      </div>
                    )}

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      {practice.videoUrl && (
                        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                          <PlayIcon className="h-4 w-4 text-emerald-600" />
                          <span className="text-xs font-medium text-emerald-700">
                            Video available
                          </span>
                        </div>
                      )}
                      {practice.content && (
                        <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-elevated px-3 py-2">
                          <LightBulbIcon className="h-4 w-4 text-success" />
                          <span className="text-xs font-medium text-text-secondary">
                            Detailed content
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                      Title
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                      Category
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                      Author
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                      Steps
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                      Analytics
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                      Date
                    </th>
                    {isCoach && (
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  <AnimatePresence>
                    {filteredPractices.map((practice, index) => (
                      <motion.tr
                        key={practice.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ delay: index * 0.02 }}
                        onClick={() => handleCardClick(practice)}
                        className="cursor-pointer transition-colors hover:bg-gray-50"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600">
                              <ShieldCheckIcon className="h-5 w-5 text-white" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-semibold text-gray-900">
                                {practice.title}
                              </div>
                              {practice.description && (
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleCardClick(practice);
                                  }}
                                  className="mt-1 text-xs font-medium text-emerald-600 hover:text-emerald-700 hover:underline"
                                >
                                  See more
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium ${getCategoryColor(practice.category)}`}
                          >
                            <TagIcon className="h-3 w-3" />
                            {categories.find(c => c.id === practice.category)?.name ||
                              practice.category}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                          {practice.createdBy && (
                            <div className="flex items-center gap-2">
                              <UserIcon className="h-4 w-4 text-gray-400" />
                              <span>
                                {practice.createdByFirstName || practice.createdByLastName
                                  ? `${practice.createdByFirstName || ''} ${practice.createdByLastName || ''}`.trim()
                                  : practice.createdByName || ''}
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                          {practice.steps && practice.steps.length > 0 ? (
                            <div className="flex items-center gap-1">
                              <CheckCircleIcon className="h-4 w-4 text-emerald-600" />
                              <span>{practice.steps.length} steps</span>
                            </div>
                          ) : (
                            <span className="text-gray-400">No steps</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1">
                              <EyeIcon className="h-4 w-4 text-gray-400" />
                              <span>{practice.views || 0}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <ChartBarIcon className="h-4 w-4 text-gray-400" />
                              <span>{practice.clicks || 0}</span>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                          {practice.createdAt?.toDate
                            ? new Date(practice.createdAt.toDate()).toLocaleDateString()
                            : 'Recently'}
                        </td>
                        {isCoach && (
                          <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
                            <div className="flex items-center gap-2">
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={e => {
                                  e.stopPropagation();
                                  handleEdit(practice);
                                }}
                                className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-50"
                                aria-label="Edit practice"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={e => {
                                  e.stopPropagation();
                                  handleDeleteClick(practice);
                                }}
                                className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50"
                                aria-label="Delete practice"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </motion.button>
                            </div>
                          </td>
                        )}
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {filteredPractices.length === 0 && (
          <div className="py-16 text-center">
            <BookOpenIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <h3 className="mb-2 text-lg font-semibold text-gray-700">
              {searchTerm || selectedCategory !== 'all'
                ? 'No practices match your filters'
                : 'No best practices yet'}
            </h3>
            <p className="text-sm text-gray-500">
              {searchTerm || selectedCategory !== 'all'
                ? 'Try adjusting your search or filters'
                : isCoach
                  ? 'Create your first best practice to get started!'
                  : 'Check back later for best practices'}
            </p>
          </div>
        )}

        {/* Add/Edit Modal */}
        <Modal
          open={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingPractice(null);
          }}
          title={editingPractice ? 'Edit Best Practice' : 'Create Best Practice'}
          description="Add a new best practice to the library"
          size="lg"
        >
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="practice-title" required>
                Title
              </Label>
              <Input
                id="practice-title"
                type="text"
                value={newPractice.title}
                onChange={e => setNewPractice({ ...newPractice, title: e.target.value })}
                placeholder="Enter practice title"
                required
              />
            </div>

            <div>
              <Label htmlFor="practice-description" required>
                Description
              </Label>
              <Textarea
                id="practice-description"
                value={newPractice.description}
                onChange={e => setNewPractice({ ...newPractice, description: e.target.value })}
                rows={3}
                placeholder="Describe the best practice..."
                required
              />
            </div>

            <div>
              <Label htmlFor="practice-category">Category</Label>
              <Select
                id="practice-category"
                value={newPractice.category}
                onChange={e => setNewPractice({ ...newPractice, category: e.target.value })}
              >
                {categories
                  .filter(c => c.id !== 'all')
                  .map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
              </Select>
            </div>

            <div>
              <Label>Steps (Optional)</Label>
              <div className="space-y-4">
                {(newPractice.steps || []).map((step, index) => {
                  const normalizedStep = normalizeStep(step);
                  return (
                    <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <div className="mb-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-success/20 text-sm font-bold text-success">
                            {index + 1}
                          </span>
                          <span className="text-sm font-semibold text-gray-700">
                            Step {index + 1}
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeStep(index)}
                          className="text-error hover:bg-error/10 hover:text-error"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <Label htmlFor={`step-title-${index}`} className="text-xs">
                            Step Title <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id={`step-title-${index}`}
                            type="text"
                            value={normalizedStep.title}
                            onChange={e => updateStep(index, 'title', e.target.value)}
                            placeholder={`e.g., Identify the problem`}
                            className="mt-1"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor={`step-description-${index}`} className="text-xs">
                            Description
                          </Label>
                          <Textarea
                            id={`step-description-${index}`}
                            value={normalizedStep.description}
                            onChange={e => updateStep(index, 'description', e.target.value)}
                            placeholder={`Describe what to do in this step...`}
                            rows={2}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
                <Button
                  type="button"
                  variant="outline"
                  onClick={addStep}
                  className="w-full border-dashed"
                  leftIcon={<PlusIcon className="h-4 w-4" />}
                >
                  Add Step
                </Button>
              </div>
            </div>

            <div>
              <Label htmlFor="practice-content">Detailed Content (Optional)</Label>
              <Textarea
                id="practice-content"
                value={newPractice.content}
                onChange={e => setNewPractice({ ...newPractice, content: e.target.value })}
                rows={6}
                placeholder="Add detailed content, explanations, or additional information..."
              />
            </div>

            <div>
              <Label htmlFor="practice-video">Video (Optional)</Label>
              <div className="space-y-3">
                {videoPreview && (
                  <div className="relative rounded-lg border border-border bg-surface-elevated p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <VideoCameraIcon className="h-5 w-5 text-success" />
                        <div>
                          <p className="text-sm font-medium text-text-primary">
                            {videoFile?.name || 'Video uploaded'}
                          </p>
                          <p className="text-xs text-text-tertiary">
                            {videoFile
                              ? `${(videoFile.size / (1024 * 1024)).toFixed(2)} MB`
                              : 'Existing video'}
                          </p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setVideoFile(null);
                          setVideoPreview(null);
                          setNewPractice({ ...newPractice, videoUrl: '' });
                        }}
                        className="text-error hover:bg-error/10 hover:text-error"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </Button>
                    </div>
                    {videoPreview && !videoFile && (
                      <video
                        src={videoPreview}
                        controls
                        className="mt-3 w-full rounded-lg"
                        style={{ maxHeight: '300px' }}
                      />
                    )}
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <label
                    htmlFor="video-upload"
                    className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border bg-surface-elevated px-4 py-3 text-sm font-medium text-text-secondary transition-all hover:border-success hover:bg-success/5 hover:text-success"
                  >
                    <VideoCameraIcon className="h-5 w-5" />
                    <span>{videoFile ? 'Change Video' : 'Upload Video'}</span>
                  </label>
                  <input
                    id="video-upload"
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    className="hidden"
                  />
                  {uploadingVideo && (
                    <div className="flex items-center gap-2 text-sm text-text-tertiary">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-success/20 border-t-success"></div>
                      <span>Uploading...</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-text-tertiary">
                  Supported formats: MP4, WebM, MOV. Max size: 100MB
                </p>
              </div>
            </div>

            <ModalFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowModal(false);
                  setEditingPractice(null);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={uploadingVideo || savingPractice}>
                {uploadingVideo || savingPractice ? (
                  <span className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/20 border-t-white"></div>
                    {uploadingVideo
                      ? 'Uploading video...'
                      : savingPractice
                        ? 'Saving...'
                        : 'Processing...'}
                  </span>
                ) : editingPractice ? (
                  'Update Practice'
                ) : (
                  'Create Practice'
                )}
              </Button>
            </ModalFooter>
          </form>
        </Modal>

        {/* Detail Modal - Shows full practice details */}
        <AnimatePresence>
          {showDetailModal && selectedPractice && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
              onClick={() => {
                setShowDetailModal(false);
                setSelectedPractice(null);
              }}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 20 }}
                onClick={e => e.stopPropagation()}
                className="relative max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl"
              >
                {/* Header */}
                <div className="border-b border-gray-200 bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="mb-2 flex items-center gap-2">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium ${getCategoryColor(selectedPractice.category)}`}
                        >
                          <TagIcon className="h-3 w-3" />
                          {categories.find(c => c.id === selectedPractice.category)?.name ||
                            selectedPractice.category}
                        </span>
                        {selectedPractice.createdAt && (
                          <div className="flex items-center gap-1 text-xs text-white/80">
                            <ClockIcon className="h-3 w-3" />
                            <span>
                              {selectedPractice.createdAt?.toDate
                                ? new Date(selectedPractice.createdAt.toDate()).toLocaleDateString()
                                : 'Recently'}
                            </span>
                          </div>
                        )}
                      </div>
                      <h2 className="text-2xl font-bold text-white">{selectedPractice.title}</h2>
                      {selectedPractice.createdBy && (
                        <p className="mt-2 text-sm text-white/90">
                          By{' '}
                          {selectedPractice.createdByFirstName || selectedPractice.createdByLastName
                            ? `${selectedPractice.createdByFirstName || ''} ${selectedPractice.createdByLastName || ''}`.trim()
                            : selectedPractice.createdByName || ''}{' '}
                          ({selectedPractice.createdBy})
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setShowDetailModal(false);
                        setSelectedPractice(null);
                      }}
                      className="rounded-lg p-2 text-white/90 transition-colors hover:bg-white/20"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="max-h-[calc(90vh-200px)] overflow-y-auto px-6 py-6">
                  {/* Description */}
                  <div className="mb-6">
                    <h3 className="mb-2 text-lg font-semibold text-gray-900">Description</h3>
                    <p className="leading-relaxed text-gray-700">{selectedPractice.description}</p>
                  </div>

                  {/* Video */}
                  {selectedPractice.videoUrl && (
                    <div className="mb-6">
                      <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                        <VideoCameraIcon className="h-5 w-5 text-emerald-600" />
                        Video
                      </h3>
                      <div className="relative overflow-hidden rounded-xl bg-black">
                        <video
                          src={selectedPractice.videoUrl}
                          controls
                          className="w-full"
                          style={{ maxHeight: '500px' }}
                          onPlay={() => {
                            // Track video play analytics
                            const practiceRef = doc(
                              firestore,
                              'bestPractices',
                              selectedPractice.id
                            );
                            updateDoc(practiceRef, {
                              clicks: increment(1),
                            });
                            addDoc(collection(firestore, 'bestPracticesAnalytics'), {
                              practiceId: selectedPractice.id,
                              practiceTitle: selectedPractice.title,
                              userId: currentUser?.uid || 'anonymous',
                              userEmail: currentUser?.email || 'anonymous',
                              userRole: userRole || 'unknown',
                              action: 'video_play',
                              timestamp: serverTimestamp(),
                            }).catch(console.error);
                          }}
                        >
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    </div>
                  )}

                  {/* Steps */}
                  {selectedPractice.steps && selectedPractice.steps.length > 0 && (
                    <div className="mb-6">
                      <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                        <CheckCircleIcon className="h-5 w-5 text-emerald-600" />
                        Steps ({selectedPractice.steps.length})
                      </h3>
                      <div className="space-y-3">
                        {selectedPractice.steps.map((step, idx) => {
                          const normalizedStep = normalizeStep(step);
                          return (
                            <div
                              key={idx}
                              className="flex items-start gap-4 rounded-lg border border-emerald-100 bg-emerald-50/50 p-4"
                            >
                              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-emerald-600 text-sm font-bold text-white">
                                {idx + 1}
                              </div>
                              <div className="flex-1">
                                <h4 className="mb-1 font-bold text-gray-900">
                                  {normalizedStep.title}
                                </h4>
                                {normalizedStep.description && (
                                  <p className="leading-relaxed text-gray-700">
                                    {normalizedStep.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Detailed Content */}
                  {selectedPractice.content && (
                    <div className="mb-6">
                      <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                        <LightBulbIcon className="h-5 w-5 text-emerald-600" />
                        Detailed Content
                      </h3>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <p className="whitespace-pre-wrap leading-relaxed text-gray-700">
                          {selectedPractice.content}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Analytics */}
                  {(selectedPractice.views > 0 || selectedPractice.clicks > 0) && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700">
                        <ChartBarIcon className="h-4 w-4" />
                        Analytics
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Total Views</p>
                          <p className="text-2xl font-bold text-emerald-600">
                            {selectedPractice.views || 0}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Total Clicks</p>
                          <p className="text-2xl font-bold text-teal-600">
                            {selectedPractice.clicks || 0}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer Actions */}
                {isCoach && (
                  <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
                    <div className="flex items-center justify-end gap-3">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowDetailModal(false);
                          handleEdit(selectedPractice);
                        }}
                        leftIcon={<PencilIcon className="h-4 w-4" />}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowDetailModal(false);
                          handleDeleteClick(selectedPractice);
                        }}
                        className="border-error/20 text-error hover:bg-error/10"
                        leftIcon={<TrashIcon className="h-4 w-4" />}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete Confirmation Modal */}
        <DeleteConfirmation
          open={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setPracticeToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          title="Delete Best Practice"
          message="Are you sure you want to delete this best practice? This action cannot be undone."
          itemName={practiceToDelete?.title}
        />
      </CardContent>
    </Card>
  );
};

export default BestPractices;
